import { TextField as MuiTextField, TextFieldProps } from '@mui/material';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppInputCustomProps, CustomFieldProps } from './types';
import useDebouncer from '@hooks/useDebounce';
import DisabledTooltip from '@components/DisabledTooltip';
import ClearInputAdornment from './ClearInputAdornment';

import styles from './Form.module.css';
import { sanitizeInput } from '@/Infrastructure/Utilities/string.type';
const classNamesCtx = classNames.bind(styles);

type AppTextFieldProps = TextFieldProps & AppInputCustomProps;

/**
 * Wrapper around MUI TextField to provide application-wide default behaviors.
 */
export default function TextField({ customProps, ...props }: AppTextFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const textFieldClass = classNamesCtx({
    input: true,
    dirty: customProps.isDirty,
    error: props.error,
    disabled: props.disabled,
    ['cell-input']: customProps.enableCellStyles,
    [props.className ?? '']: Boolean(props.className)
  });

  const inputElClass = classNamesCtx({
    root: true,
    input: true,
    standard: props.variant === 'standard',
    [customProps.extendedClassName ?? '']: Boolean(customProps.extendedClassName),
    'hidden-label': !customProps.labelText
  });

  useEffect(() => {
    if (props.focused) {
      inputRef.current?.focus();
    }
  }, [props.focused]);

  return (
    <DisabledTooltip
      message={props.disabled ? customProps.disabledReason : undefined}
      className={customProps.disabledReason ? textFieldClass : undefined}>
      <MuiTextField
        helperText={props.helperText}
        autoComplete='one-time-code'
        multiline={customProps.textArea}
        rows={customProps.textArea ? (customProps.rows ?? 4) : 1}
        {...props}
        className={textFieldClass}
        label={
          <FormLabel
            text={customProps.labelText}
            error={props.error && !props.disabled}
            errorText={customProps.errorText}
            required={customProps.required}
          />
        }
        onBlur={customProps.onBlur}
        onKeyDown={(event) => {
          if (customProps.stopPropagation) {
            event.stopPropagation();
          }
        }}
        sx={{
          width: customProps.disabledReason ? '100%' : undefined
        }}
        slotProps={{
          htmlInput: {
            maxLength: customProps.maxLength,
            multiple: customProps.multiple,
            ref: inputRef
          },
          input: {
            sx: {
              height: customProps.textArea ? undefined : '32px',
              paddingTop: customProps.textArea ? '8.5px' : undefined
            },
            className: inputElClass,
            startAdornment: customProps.startAdornment,
            endAdornment: (
              <ClearInputAdornment
                onClear={() => {
                  customProps.onClear?.();
                  inputRef.current?.focus();
                }}
                inputValue={props.value}
                inputIsDisabled={props.disabled}
              />
            )
          }
        }}
      />
    </DisabledTooltip>
  );
}

function asTextInput() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error, touched } = form.getFieldMeta<string | null>(field.name);

    const { dirty } = form;
    const { onBlurExtension } = props;

    const [isErrorState, setIsErrorState] = useState(false);
    const isDirty = useMemo(() => {
      if (dirty) {
        return value !== initialValue;
      }
      return false;
    }, [value, initialValue, dirty]);

    /** Keeping track of the input state locally for performance reasons when typing into the input */
    const [myValue, setMyValue] = useState<string>(value ?? '');
    const myValueRef = useRef(myValue);
    myValueRef.current = myValue;

    /** Tracks the last value we pushed into Formik so we can ignore echoes of our own writes
     *  and avoid clobbering newer keystrokes the user typed while the write was in-flight. */
    const lastSyncedRef = useRef<string>(value ?? '');

    /** If the formik value changes outside of this component, update our local state to reflect */
    useEffect(() => {
      const incoming = value ?? '';
      if (incoming === lastSyncedRef.current) {
        return;
      }
      lastSyncedRef.current = incoming;
      setMyValue(incoming);
    }, [field.name, value]);

    /** When the local state has changed, compare to the form value. After a short delay, update the form if the value differs. */
    useDebouncer(
      () => {
        if (myValue !== (value ?? '')) {
          lastSyncedRef.current = myValue;
          void form.setFieldTouched(field.name, true);
          void form.setFieldValue(field.name, myValue, true);
        }
      },
      [myValue],
      100
    );

    /** Flush the pending local value to Formik immediately on blur so tabbing out always commits */
    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        const current = myValueRef.current.trim();
        setMyValue(current);
        if (current !== (value ?? '')) {
          lastSyncedRef.current = current;
          void form.setFieldTouched(field.name, true);
          void form.setFieldValue(field.name, current, true);
        }
        // onBlurExtension reads event.target.value, so we need to set it to the trimmed value before calling it
        event.target.value = current;
        onBlurExtension?.(event);
      },
      [value, form, field.name, onBlurExtension]
    );

    useDebouncer(
      () => {
        setIsErrorState(touched && Boolean(error));
      },
      [touched, error],
      100
    );

    return (
      <TextField
        name={field.name}
        placeholder={props.placeholder}
        className={props.className}
        variant={props.variant}
        slotProps={{
          input: {
            startAdornment: props.startAdornmentIcon,
            endAdornment: props.endAdornmentIcon
          }
        }}
        value={myValue}
        onChange={(event) => {
          setMyValue(sanitizeInput(event.target.value));
        }}
        error={isErrorState}
        customProps={{
          labelText: props.label ?? '',
          isDirty: isDirty,
          errorText: error,
          onClear: () => {
            setMyValue('');
          },
          textArea: props.textArea,
          rows: props.rows,
          disabledReason: props.disabledReason,
          required: props.required,
          onBlur: handleBlur
        }}
        disabled={form.isSubmitting || props.disabled}
        helperText={props.helperText}
      />
    );
  };
}

export const FormikTextInput = asTextInput();
